import type React from 'react';
import { useState } from 'react';
import { Tile } from '../../game/tile';
import { Avatar } from '../../identity/avatar';
import { useWordsHappenTheme } from '../../theme';
import styles from './priority-card.module.scss';

export interface PriorityCardProps {
  kind: 'your-turn' | 'daily-streak';
  title: string;
  subtitle?: string;
  badge?: { text: string; tone: 'urgent' | 'reward' };
  opponent?: { name: string; color: string };
  streak?: number;
  onClick: () => void;
  className?: string;
}

const FlameIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={styles.flameIcon}>
    <path
      d="M12 2c-3 4.5-5 7-5 11a5 5 0 0 0 10 0c0-3-1.5-5-1.5-5s-.5 3-2.5 3c-1.5 0-1-3.5-1-3.5S9 10 9 13a3 3 0 0 0 6 0c0-4-3-11-3-11z"
      fill="var(--wh-coral)"
    />
  </svg>
);

const DECORATIVE_TILES = ['W', 'O', 'R'] as const;

export const PriorityCard: React.FC<PriorityCardProps> = ({
  kind,
  title,
  subtitle,
  badge,
  opponent,
  streak,
  onClick,
  className,
}) => {
  const { theme } = useWordsHappenTheme();
  const [pressed, setPressed] = useState(false);

  const isMeadow = theme === 'meadow';
  const isPressed = isMeadow && pressed;

  const ariaLabel = badge?.tone === 'urgent' ? `${title}, urgent` : title;

  const cls = [styles.card, isMeadow ? styles.meadow : styles.flat, isPressed ? styles.pressed : null, className]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={cls}
      aria-label={ariaLabel}
      onClick={onClick}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
    >
      <div className={styles.media} aria-hidden="true">
        {kind === 'your-turn' && opponent ? (
          <Avatar name={opponent.name} color={opponent.color} size={48} />
        ) : kind === 'daily-streak' ? (
          <div className={styles.streakMedia}>
            <FlameIcon size={22} />
            {streak != null && <span className={styles.streakCount}>{streak}</span>}
          </div>
        ) : null}
      </div>

      <div className={styles.body}>
        {badge && (
          <span
            className={[styles.badge, badge.tone === 'urgent' ? styles.badgeUrgent : styles.badgeReward].join(' ')}
            aria-hidden="true"
          >
            {badge.text}
          </span>
        )}
        <span className={styles.title}>{title}</span>
        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        {kind === 'your-turn' && (
          <div className={styles.tiles} aria-hidden="true">
            {DECORATIVE_TILES.map((l) => (
              <Tile key={l} letter={l} size={26} mini />
            ))}
          </div>
        )}
      </div>
    </button>
  );
};
